// © 2025 Ananda Kumar. All rights reserved.
// Unauthorized copying of this file, via any medium, is strictly prohibited.

import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { Observable, BehaviorSubject, of, ReplaySubject } from 'rxjs';
import { tap, catchError, shareReplay, map } from 'rxjs/operators';
import { TenantService } from './tenant.service';
import { NavigationConfig, SectionConfig } from '../app/models/navigationConfig.model';

export interface Configuration {
  coverImageUrl?: string;
  galleryImageUrl?: string;
  eventType?: string;
  eventDate?: string;
  eventVenue?: string;
  eventTitle?: string;
  id?: string;
  whatsappChannelUrl?: string;
  whatsappChannel?: string;
  eventOverview?: unknown;
  menu?: import('../app/models/domain.models').MenuConfigItem[];
  // Allow additional runtime fields from the API
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class ConfigurationService {
  private configurationSubject = new BehaviorSubject<Configuration | null>(null);
  private configuration$ = this.configurationSubject.asObservable();
  private navigationConfigSubject = new BehaviorSubject<NavigationConfig | null>(null);
  private isLoaded = false;
  private isLoading = false;
  private ongoingRequest: Observable<Configuration | null> | null = null;
  private readonly CACHE_DURATION = 20 * 60 * 1000; // 20 minutes

  constructor(private apiService: ApiService, private tenantService: TenantService) { }

  private getCacheKey(): string {
    const tenantId = this.tenantService.getTenantId() || this.tenantService.getTenantKey();
    return `configuration_${tenantId}`;
  }

  getConfiguration(loadFromCache: boolean = true): Observable<Configuration | null> {
    const cacheKey = this.getCacheKey();
    // Check sessionStorage for cached config
    const cached = sessionStorage.getItem(cacheKey);
    if (loadFromCache && cached) {
      try {
        const parsed = JSON.parse(cached);
        if (parsed.expiresAt && Date.now() < parsed.expiresAt) {
          this.configurationSubject.next(parsed.data);
          // Build nav config immediately from cached configuration
          this.navigationConfigSubject.next(
            this.buildNavigationConfigFromMenu(parsed.data?.['menu'] ?? [])
          );
          this.isLoaded = true;
          return this.configuration$;
        } else {
          sessionStorage.removeItem(cacheKey);
        }
      } catch {
        sessionStorage.removeItem(cacheKey);
      }
    }

    // If an API call is already in progress, return the same observable
    if (this.ongoingRequest) {
      return this.ongoingRequest;
    }

    this.isLoading = true;
    const request = this.apiService.getData('configuration/get').pipe(
      tap((config: Configuration) => {
        this.configurationSubject.next(config);
        // Build nav config as soon as configuration is loaded
        this.navigationConfigSubject.next(
          this.buildNavigationConfigFromMenu(config?.['menu'] ?? [])
        );
        this.isLoaded = true;
        this.isLoading = false;
        // Cache the config for 20 minutes
        sessionStorage.setItem(cacheKey, JSON.stringify({
          data: config,
          expiresAt: Date.now() + this.CACHE_DURATION
        }));
        this.ongoingRequest = null;
      }),
      catchError((error) => {
        console.error('Failed to load configuration:', error);
        this.configurationSubject.next(null);
        this.isLoaded = true;
        this.isLoading = false;
        this.ongoingRequest = null;
        return of(null);
      }),
      shareReplay(1)
    );
    this.ongoingRequest = request;
    return request;
  }

  getMenu(): Observable<any> {
    return this.getConfiguration().pipe(
      map(config => config?.['menu'])
    );
  }

  /**
   * Get menu configuration by menuKey
   * @param menuKey The key of the menu to retrieve (e.g., 'gallery', 'kithAndKin')
   * @returns Observable with the menu configuration object or null if not found
   */
  getMenuByKey(menuKey: string): Observable<any> {
    return this.getConfiguration().pipe(
      map(config => {
        const menuItems = config?.['menu'] ?? [];
        return menuItems.find((item: any) => item.menu === menuKey) || null;
      })
    );
  }

  getNavigationConfig(): Observable<NavigationConfig> {
    return this.getConfiguration().pipe(
      map((config) => {
        const existing = this.navigationConfigSubject.getValue();
        if (existing) {
          return existing;
        }
        const built = this.buildNavigationConfigFromMenu(config?.['menu'] ?? []);
        this.navigationConfigSubject.next(built);
        return built;
      }),
      shareReplay(1)
    );
  }

  private buildNavigationConfigFromMenu(menuItems: any[]): NavigationConfig {
    const navigationConfig = new NavigationConfig();

    navigationConfig.kithKin = {
      ...new SectionConfig(),
      show: menuItems.some((item: any) => item.menu === 'kithAndKin' && item.isActive),
      menuItems: menuItems.find((item: any) => item.menu === 'kithAndKin' && item.isActive)?.menuItems || []
    };

    navigationConfig.galleryCard = {
      ...new SectionConfig(),
      show: menuItems.some((x: any) => x.menu === 'gallery' && x.isActive),
      menuItems: [
        {
          id: 0,
          label: 'Wedding Gallery',
          icon: 'cs-icon i-playing',
          routeUrl: 'gallery'
        }
      ]
    };

    navigationConfig.eventOrganisers = {
      ...new SectionConfig(),
      show: menuItems.some((x: any) => x.menu === 'eventOrganisers' && x.isActive)
    };

    navigationConfig.accommodation = {
      ...new SectionConfig(),
      show: menuItems.some((x: any) => x.menu === 'accommodation' && x.isActive)
    };


    return navigationConfig;
  }

  getCoverImageUrl(): Observable<string | null> {
    return new Observable(observer => {
      this.getConfiguration().subscribe({
        next: (config) => {
          if (config && config.coverImageUrl) {
            observer.next(config.coverImageUrl);
          } else {
            observer.next(null);
          }
          observer.complete();
        },
        error: (error) => {
          console.error('Error getting cover image URL:', error);
          observer.next(null);
          observer.complete();
        }
      });
    });
  }

  clearCache(): void {
    this.isLoaded = false;
    this.isLoading = false;
    this.configurationSubject.next(null);
    const cacheKey = this.getCacheKey();
    sessionStorage.removeItem(cacheKey);
  }
} 